import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { refreshSiteSettings } from "@/hooks/useSiteSettings";
import { Settings, Mail, Phone, MapPin, Save, Loader2, Building2, Globe as GlobeIcon } from "lucide-react";

const FIELDS = [
  { key: "contact_email", label: "Email", icon: Mail, placeholder: "hello@hawkam.eu", type: "email" },
  { key: "contact_phone", label: "Phone", icon: Phone, placeholder: "+966 55 000 0000" },
  { key: "whatsapp", label: "WhatsApp", icon: Phone, placeholder: "+966 55 000 0000" },
  { key: "telegram", label: "Telegram (@handle or URL)", icon: Phone, placeholder: "@hawkam or https://t.me/hawkam" },
  { key: "address_ar", label: "Address (AR)", icon: MapPin, placeholder: "الرياض، المملكة العربية السعودية" },
  { key: "address_en", label: "Address (EN)", icon: MapPin, placeholder: "Riyadh, Saudi Arabia" },
  { key: "working_hours_ar", label: "Working Hours (AR)", icon: Building2, placeholder: "الأحد – الخميس، 9ص – 6م" },
  { key: "working_hours_en", label: "Working Hours (EN)", icon: Building2, placeholder: "Sun – Thu, 9am – 6pm" },
  { key: "tax_id", label: "VAT / Tax ID", icon: Building2, placeholder: "3000123456" },
  { key: "cr_number", label: "Commercial Registration (CR)", icon: Building2, placeholder: "1010000000" },
  { key: "linkedin_url", label: "LinkedIn URL", icon: GlobeIcon, placeholder: "https://linkedin.com/company/hawkam" },
  { key: "twitter_url", label: "X / Twitter URL", icon: GlobeIcon, placeholder: "https://x.com/hawkam" },
  { key: "telegram", label: "Telegram (@handle or URL)", icon: GlobeIcon, placeholder: "@hawkam or https://t.me/hawkam" },
  { key: "youtube_url", label: "YouTube URL", icon: GlobeIcon, placeholder: "https://youtube.com/@hawkam" },
  { key: "years_experience", label: "Homepage · Years of experience", icon: Building2, type: "number", placeholder: "12" },
  { key: "clients_count", label: "Homepage · Clients served", icon: Building2, type: "number", placeholder: "80" },
  { key: "certifications_count", label: "Homepage · Certifications count", icon: Building2, type: "number", placeholder: "3" },
  { key: "certifications_label_ar", label: "Homepage · Certifications label (AR)", icon: Building2, placeholder: "معايير عالمية" },
  { key: "certifications_label_en", label: "Homepage · Certifications label (EN)", icon: Building2, placeholder: "International Standards" },
  { key: "retention_enabled", label: "Auto-delete old customer data", icon: Settings, type: "checkbox" },
  { key: "retention_days", label: "Retention period (days) — leave blank for permanent", icon: Settings, type: "number", placeholder: "e.g., 365" },
];

export default function AdminSettings() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/site-settings")
      .then((r) => setValues(r.data || {}))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const setField = (key, val) => setValues((s) => ({ ...s, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = FIELDS.reduce((acc, f) => {
        const v = values[f.key];
        if (f.type === "checkbox") {
          acc[f.key] = !!v;
        } else if (f.type === "number") {
          acc[f.key] = (v === "" || v == null) ? null : Number(v);
        } else {
          acc[f.key] = v || "";
        }
        return acc;
      }, {});
      const { data } = await api.put("/admin/site-settings", payload);
      refreshSiteSettings(data);
      toast.success("Settings saved — public site updated instantly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-settings">
      <span className="eyebrow">Site</span>
      <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
        <Settings size={26} className="text-gold" /> Site Settings
      </h1>
      <span className="divider-gold" />
      <p className="text-navy/70 max-w-2xl text-sm mb-6">
        These values feed the public footer, contact page, and structured metadata. Changes go live immediately with no redeploy — every edit is recorded in the audit log.
      </p>

      {loading ? (
        <div className="text-navy/50"><Loader2 className="animate-spin inline mr-2" /> Loading…</div>
      ) : (
        <form onSubmit={submit} className="card-elev p-8 space-y-5" data-testid="settings-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <label key={f.key} className="block">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 mb-2">
                  <f.icon size={12} className="text-gold" /> {f.label}
                </div>
                {f.type === "checkbox" ? (
                  <input
                    data-testid={`setting-${f.key}`}
                    type="checkbox"
                    className="w-5 h-5 accent-gold"
                    checked={!!values[f.key]}
                    onChange={(e) => setField(f.key, e.target.checked)}
                  />
                ) : (
                  <input
                    data-testid={`setting-${f.key}`}
                    type={f.type || "text"}
                    placeholder={f.placeholder}
                    className="field-box"
                    value={values[f.key] ?? ""}
                    onChange={(e) => setField(f.key, f.type === "number" ? (e.target.value ? Number(e.target.value) : null) : e.target.value)}
                  />
                )}
              </label>
            ))}
          </div>
          <div className="pt-4 border-t border-navy/10 flex items-center gap-3">
            <button
              type="submit"
              disabled={saving}
              className="btn-gold disabled:opacity-40"
              data-testid="settings-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save changes"}
            </button>
            <div className="text-xs text-navy/50">
              Fields left blank will be hidden from the public site.
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
